class SiteHeader extends HTMLElement {
  constructor() {
    super()
  } 

  connectedCallback() {
    this.classes = {
      active: 'f-header__mega-active',
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme
    }
    this.headerSticky = document.querySelector('.site-header__wrapper')

    this.isRTL = document.documentElement.getAttribute('dir') === 'rtl'
    this.grandLinks = this.querySelectorAll('.f-site-nav__sub-item--has-child')
    this.grandLinks && this.grandLinks.forEach((item, index) => {
        this.handleGrandLinksPosition(item)
    })

    this.initNavStacking()
  }

  disconnectedCallback() {
    if (this.navStackObserver) {
      this.navStackObserver.disconnect()
    }

    if (this.scheduleNavStacking) {
      window.removeEventListener('resize', this.scheduleNavStacking)
    }
    if (this.handleNavABTest) {
      document.removeEventListener('megaMenuABTest', this.handleNavABTest)
    }
    if (this.navStackFrame) {
      cancelAnimationFrame(this.navStackFrame)
    }
  }

  /**
   * Nav stacking: when the desktop nav links no longer fit on one line next to the
   * logo/icons, move the whole nav onto its own full-width row below them
   * (see `.site-header--nav-stacked` in header.css). Re-evaluated on every resize.
   */
  initNavStacking() {
    this.navStackClass = 'site-header--nav-stacked'
    this.navLists = Array.from(this.querySelectorAll('.site-header__menu .f-site-nav__list'))
    this.stackedNav = this.querySelector('[data-stacked-nav]')
    this.splitNavLocations = []

    if (this.stackedNav) {
      this.navLists.forEach((list) => {
        const nav = list.closest('site-nav')
        const placeholder = document.createComment('Split navigation original position')
        nav.before(placeholder)
        this.splitNavLocations.push({ nav, placeholder })
      })
    }

    if (this.navLists.length === 0) {
      return
    }

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    this.navStackFrame = null

    // Debounce to one check per animation frame, no matter how many resize events fire
    this.scheduleNavStacking = () => {
      if (this.navStackFrame) {
        cancelAnimationFrame(this.navStackFrame)
      }

      this.navStackFrame = requestAnimationFrame(() => {
        this.updateNavStacking()
      })
    }

    this.navStackObserver = new ResizeObserver(this.scheduleNavStacking)
    this.navStackObserver.observe(this)
    window.addEventListener('resize', this.scheduleNavStacking)

    // Required to allow A/B testing to work as expected: moved menus no longer
    // inherit hiding from their original wrappers. Restore and reassess them
    // synchronously when the variant is selected, before the browser paints.
    this.handleNavABTest = () => {
      this.updateNavStacking()
      if (typeof window.__calcHeight === 'function') {
        window.__calcHeight()
      }
    }
    document.addEventListener('megaMenuABTest', this.handleNavABTest)

    this.updateNavStacking()
  }

  /**
   * Returns the nav lists that are actually rendered on the page.
   * There can be 2 visible menus when the header is set to split the menu around the logo
   * There can be more than one list when the mega menu A/B test is on, but only one of them is visible at a time.
   */
  getVisibleNavLists() {
    const visibleNavLists = this.navLists.filter((list) => this.isNavVisible(list))
    return visibleNavLists
  }

  isNavVisible(element) {
    // Required to allow A/B testing to work as expected: hide-on-load uses
    // visibility:hidden, which still has client rects and must not be moved.
    const visibility = window.getComputedStyle(element).visibility
    const isVisible = element.getClientRects().length > 0 && visibility === 'visible'
    return isVisible
  }

  /**
   * Returns true when any visible nav list has top-level items on multiple lines.
   */
  navIsWrapping() {
    const visibleNavLists = this.getVisibleNavLists()
    const isWrapping = visibleNavLists.some((list) => {
      const visibleItems = Array.from(list.children).filter((item) => item.getClientRects().length > 0)

      let listIsWrapping = false

      if ( visibleItems.length >= 2 ) {
        const firstItem = visibleItems[0].getBoundingClientRect()

        // Compare within each list; separate lists can have different top edges.
        listIsWrapping = visibleItems.slice(1).some((item) => {
          const verticalOffset = Math.abs(item.getBoundingClientRect().top - firstItem.top)
          const itemIsWrapping = verticalOffset > firstItem.height / 2
          return itemIsWrapping
        })
      }

      return listIsWrapping
    })

    return isWrapping
  }

  updateNavStacking() {
    // Below the md breakpoint the nav lives in the mobile drawer, so there is nothing to stack
    if (window.matchMedia('(max-width: 767.98px)').matches) {
      this.toggleNavStacking(false)
      return
    }

    // Always measure in the un-stacked layout so we get a stable answer
    // regardless of which state the header is currently in
    const wasStacked = this.classList.contains(this.navStackClass)

    if (wasStacked) {
      this.classList.remove(this.navStackClass)
      this.restoreSplitNavigation()
    }

    const shouldStack = this.navIsWrapping()

    if (wasStacked) {
      this.classList.add(this.navStackClass)
    }

    this.toggleNavStacking(shouldStack)
  }

  toggleNavStacking(stacked) {
    const isStacked = this.classList.contains(this.navStackClass)

    // Measurement restores the menus even when the stacked state stays the same.
    if (stacked && this.stackedNav) {
      const visibleNavs = this.splitNavLocations.filter(
        ({ nav }) => this.isNavVisible(nav)
      )
      visibleNavs.forEach(({ nav }) => {
        this.stackedNav.appendChild(nav)
      })
    } else {
      this.restoreSplitNavigation()
    }

    if (isStacked === stacked) {
      return
    }

    if (stacked) {
      this.classList.add(this.navStackClass)
    } else {
      this.classList.remove(this.navStackClass)
    }

    // Header height changed; refresh the CSS var used by the sticky placeholder and dropdowns
    if (typeof window.__calcHeight === 'function') {
      window.__calcHeight()
    }
  }

  restoreSplitNavigation() {
    this.splitNavLocations?.forEach(({ nav, placeholder }) => {
      if (nav.parentNode !== placeholder.parentNode) {
        placeholder.after(nav)
      }
    })
  }

  calcDropdownHeight(dropdown) {
    if (!dropdown || !dropdown.classList.contains('f-site-nav__dropdown--mega')) {
    this.style.setProperty('--f-dropdown-height', '0px');
    return;
  }

    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty('--f-dropdown-height', rect.height + 'px');
  }

  handleMegaItemActive() {
    this.classList.add(this.classes.active, this.classes.dropdownScheme)
    this.classList.remove(this.classes.headerScheme)
    document.documentElement.style.setProperty("--f-header-height", this.clientHeight + "px")
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme)
    this.classList.add(this.classes.headerScheme)
    document.documentElement.style.setProperty("--f-header-height", this.clientHeight + "px")
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector('.f-site-nav__dropdown')
    if( dropdownLV3 ) {
      const rect = dropdownLV3.getBoundingClientRect()
      dropdownLV3.classList.remove('f-site-nav__dropdown-reversed')
      if( ( ! this.isRTL && document.documentElement.clientWidth < rect.x + rect.width + 10  )
        || ( this.isRTL && rect.x < 10 )
        ) {
          dropdownLV3.classList.add('f-site-nav__dropdown-reversed')
      }
    }
  }
}
customElements.define('site-header', SiteHeader)

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super()
  }

  connectedCallback() {
    this.header = this.closest('.site-header')
    this.classes = {
      itemActive: 'f-menu__item-active'
    }
    this.header.timeoutEnter = null
    this.header.timeoutLeave = null
  }

  onToggle(evt) {
    const { target } = evt

    const li = target.closest('.f-site-nav__item')
    const isOpen = this.mainDetailsToggle.open
    const isMega = li.classList.contains('f-site-nav__item--mega')

    if( ! isOpen ) {
      clearTimeout(this.header.timeoutEnter)
      li.classList.remove(this.classes.itemActive)
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive()
      }, 160)
    } else {
      if ( isMega ) {
        clearTimeout(this.header.timeoutLeave)
        const dropdown = li.querySelector('.f-site-nav__dropdown')
        this.header.calcDropdownHeight( dropdown )
        this.header.handleMegaItemActive()
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add( this.classes.itemActive )
        }, 160)
      } else {
        li.classList.add( this.classes.itemActive )
      }
    }

    if (this.header.headerSticky) {
      this.header.headerSticky.preventReveal = this.mainDetailsToggle.open
    }
  }
}
customElements.define('header-menu', HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    // Moving a split menu within its header must not register its listeners again.
    const header = this.closest('.site-header')
    if (this.header === header && this.classes) {
      return
    }
    this.header = this.closest('.site-header')
    this.classes = {
      itemActive: 'f-menu__item-active',
      itemClosed: 'f-menu__item-closed'
    }

    this.megaItems = this.querySelectorAll('.f-site-nav__item--has-child');

    this.megaItems?.forEach((megaItem) => {
      const dropdown = megaItem.querySelector('.f-site-nav__dropdown--level2');
      this.setDropdownTabbable(dropdown, false);
    });

    this.timeoutLeave = null
    this.isHover = this.header && this.header.classList.contains('show-dropdown-menu-on-hover')

    if (this.isHover) {
      this.megaItems?.forEach((megaItem) => {
        megaItem.onMouseEnter = (evt) => this.onMenuItemEnter(evt);
        megaItem.onMouseLeave = (evt) => this.onMenuItemLeave(evt);

        megaItem.addEventListener('mouseenter', megaItem.onMouseEnter);
        megaItem.addEventListener('mouseleave', megaItem.onMouseLeave);
      });
    }

    this.megaItems && this.megaItems.forEach((megaItem) => {
      megaItem.addEventListener('focusout', (evt) => {
        const next = evt.relatedTarget;

        if (next && megaItem.contains(next)) return;

        this.closeMegaItem(megaItem, { returnFocus: false });
      })
    })

    this.megaItems && this.megaItems.forEach((megaItem) => {
      const trigger = megaItem.querySelector('summary.f-site-nav__link') || megaItem.querySelector('a.f-site-nav__link');

      if (!trigger) return;

      megaItem.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;

        const isOpen = megaItem.classList.contains(this.classes.itemActive);
        if (!isOpen) return;

        e.preventDefault();
        e.stopPropagation();

        this.closeMegaItem(megaItem, { returnFocus: true });
        megaItem.classList.add(this.classes.itemClosed);
        trigger?.focus({ preventScroll: true });
      });

      const getDropdown = () => megaItem.querySelector('.f-site-nav__dropdown--level2');

      if (this.isHover) {
        trigger.setAttribute('aria-expanded', 'false');
        megaItem.onTriggerClick = (evt) => this.onMenuTriggerClick(evt, megaItem);
        trigger.addEventListener('click', megaItem.onTriggerClick);
      }

      trigger.addEventListener('keydown', (e) => {
        const isSpace = e.key === ' ' || e.key === 'Spacebar';
        const isEscape = e.key === 'Escape';
        if (!isSpace && !isEscape) return;

        e.preventDefault();
        e.stopPropagation();

        const dropdown = getDropdown();
        if (!dropdown) return;

        // ESC closes immediately and returns focus
        if (isEscape) {
          const isOpen = megaItem.classList.contains(this.classes.itemActive);
          if (!isOpen) return;

          this.closeMegaItem(megaItem, { returnFocus: true });
          megaItem.classList.add(this.classes.itemClosed);
          return;
        }

        // SPACE toggles open/close (NO focusing here)
        const isOpen = megaItem.classList.contains(this.classes.itemActive);

        if (isOpen) {
          this.closeMegaItem(megaItem, { returnFocus: true });
          megaItem.classList.add(this.classes.itemClosed);
          trigger.dataset.pendingMegaFocus = '';
          return;
        }

        // OPEN MENU
        megaItem.classList.remove(this.classes.itemClosed);
        clearTimeout(this.timeoutLeave);
        this.header.calcDropdownHeight(dropdown);
        this.header.handleMegaItemActive();
        megaItem.classList.add(this.classes.itemActive);
        megaItem.isClickOpen = true;
        this.setDropdownTabbable(dropdown, true);
        trigger.setAttribute('aria-expanded', 'true');

        // tell keyup to move focus into menu
        trigger.dataset.pendingMegaFocus = '1';
      });

      trigger.addEventListener('keyup', (e) => {
        const isSpace = e.key === ' ' || e.key === 'Spacebar';
        if (!isSpace) return;

        e.preventDefault();
        e.stopPropagation();

        // only do focus jump if we opened on keydown
        if (trigger.dataset.pendingMegaFocus !== '1') return;
        trigger.dataset.pendingMegaFocus = '';

        const dropdown = getDropdown();
        if (!dropdown) return;

        // if something closed it, bail
        if (!megaItem.classList.contains(this.classes.itemActive)) return;

        requestAnimationFrame(() => {
          let first = dropdown.querySelector('.f-site-nav__sub-item > a[href]');
          if (!first) {
            first = dropdown.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
          }
          if (!first) return;

          // ensure it's focusable if some tabindex lingering exists
          if (first.getAttribute('tabindex') === '-1') {
            first.removeAttribute('tabindex');
            first.removeAttribute('data-prev-tabindex');
          }

          first.focus({ preventScroll: true });

          // if theme steals focus back, force once more
          if (document.activeElement !== first) {
            setTimeout(() => first.focus({ preventScroll: true }), 0);
          }
        });
      });

    });
  }

  setDropdownTabbable(dropdown, enabled) {
    if (!dropdown) return;

    const focusables = dropdown.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]'
    );

    focusables.forEach((el) => {
      const existing = el.getAttribute('tabindex');

      if (enabled) {
        const prev = el.getAttribute('data-prev-tabindex');

        if (prev !== null) {
          if (prev === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', prev);
          }
          el.removeAttribute('data-prev-tabindex');
        } else {
          if (existing === '-1') el.removeAttribute('tabindex');
        }
      } else {
        if (!el.hasAttribute('data-prev-tabindex')) {
          el.setAttribute('data-prev-tabindex', existing ?? '');
        }
        el.setAttribute('tabindex', '-1'); 
      }
    });

    dropdown.setAttribute('aria-hidden', enabled ? 'false' : 'true');
  }

  onMenuTriggerClick(evt, megaItem) {
    const dropdown = megaItem.querySelector('.f-site-nav__dropdown--level2');

    if (!dropdown) return;

    evt.preventDefault();
    clearTimeout(megaItem.timeoutEnter);

    const isOpen = megaItem.isClickOpen && megaItem.classList.contains(this.classes.itemActive);

    if (isOpen) {
      this.closeMegaItem(megaItem, { returnFocus: false });
      megaItem.classList.add(this.classes.itemClosed);
      return;
    }

    this.megaItems?.forEach((item) => {
      if (item !== megaItem) {
        this.closeMegaItem(item, { returnFocus: false });
      }
    });

    clearTimeout(this.timeoutLeave);
    megaItem.classList.remove(this.classes.itemClosed);
    megaItem.classList.add(this.classes.itemActive);
    megaItem.isClickOpen = true;
    this.setDropdownTabbable(dropdown, true);
    evt.currentTarget.setAttribute('aria-expanded', 'true');
    this.header.calcDropdownHeight(dropdown);
    this.header.handleMegaItemActive();
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;
    const dropdown = target.querySelector('.f-site-nav__dropdown');

    if (dropdown) {
      target.classList.remove(this.classes.itemClosed);
      this.setDropdownTabbable(dropdown, true);
      target.querySelector('a.f-site-nav__link')?.setAttribute('aria-expanded', 'true');
      this.header.calcDropdownHeight(dropdown);
      this.header.handleMegaItemActive();

      target.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
      }, 160);
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;
    const dropdown = target.querySelector('.f-site-nav__dropdown');

    if (dropdown) {
      this.setDropdownTabbable(dropdown, false);
      target.querySelector('a.f-site-nav__link')?.setAttribute('aria-expanded', 'false');
    }

    clearTimeout(target.timeoutEnter);
    target.classList.remove(this.classes.itemActive);
    target.isClickOpen = false;
    this.scheduleHeaderDeactivation();
  }

  closeMegaItem(megaItem, { returnFocus = true } = {}) {
    const dropdown = megaItem.querySelector('.f-site-nav__dropdown--level2');
    const trigger =
      megaItem.querySelector(':scope > summary.f-site-nav__link') ||
      megaItem.querySelector(':scope > a.f-site-nav__link');

    if (returnFocus && dropdown && dropdown.contains(document.activeElement)) {
      trigger?.focus();
    }

    clearTimeout(megaItem.timeoutEnter);
    megaItem.classList.remove(this.classes.itemActive);
    megaItem.isClickOpen = false;
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (dropdown) {
      this.setDropdownTabbable(dropdown, false);
    }

    this.scheduleHeaderDeactivation();
  }

  scheduleHeaderDeactivation() {
    clearTimeout(this.timeoutLeave);

    this.timeoutLeave = setTimeout(() => {
      let hasOpenDropdown = false;

      for (let i = 0; i < this.megaItems.length; i++) {
        const megaItem = this.megaItems[i];
        const dropdown = megaItem.querySelector('.f-site-nav__dropdown--level2');

        const isMenuActive = megaItem.classList.contains(this.classes.itemActive);
        const isDropdownVisible = dropdown?.getAttribute('aria-hidden') === 'false';

        if (isMenuActive || isDropdownVisible) {
          hasOpenDropdown = true;
          break;
        }
      }

      if (!hasOpenDropdown) {
        this.header.handleMegaItemDeactive();
      }
    }, 80);
  }

  closeMegaDropdowns() {
    this.megaItems?.forEach((megaItem) => {
      this.closeMegaItem(megaItem, { returnFocus: false });
    });
  } 

  disconnectedCallback() {
    this.megaItems?.forEach((megaItem) => {
      megaItem.removeEventListener('mouseenter', megaItem.onMouseEnter);
      megaItem.removeEventListener('mouseleave', megaItem.onMouseLeave);

      const trigger = megaItem.querySelector('a.f-site-nav__link');
      if (trigger && megaItem.onTriggerClick) {
        trigger.removeEventListener('click', megaItem.onTriggerClick);
      }

      clearTimeout(megaItem.timeoutEnter);
    });

    clearTimeout(this.timeoutLeave);
  }
}
customElements.define('site-nav', SiteNav)
